import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  CircularProgress,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Chip,
  Divider,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tabs,
  Tab,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ConstructionIcon from '@mui/icons-material/Construction';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SyncIcon from '@mui/icons-material/Sync';
import SearchIcon from '@mui/icons-material/Search';
import FolderIcon from '@mui/icons-material/Folder';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import VideocamIcon from '@mui/icons-material/Videocam';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionIcon from '@mui/icons-material/Description';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FilterListIcon from '@mui/icons-material/FilterList';
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined';
import CloudQueueOutlinedIcon from '@mui/icons-material/CloudQueueOutlined';
import GrainOutlinedIcon from '@mui/icons-material/GrainOutlined';
import ThunderstormOutlinedIcon from '@mui/icons-material/ThunderstormOutlined';

const API_BASE = 'https://apiexterna.diariodeobra.app/v1';

/* ─── Subcomponente: Cadastros (Usuários + Mão de Obra + Equipamentos) ─── */
function CadastrosView({ data }) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [perfilFilter, setPerfilFilter] = useState('todos');
  const [tab, setTab] = useState(0);

  const usuarios = useMemo(() => data?.usuarios || [], [data]);
  const maoDeObra = useMemo(() => data?.listaMaoDeObra || {}, [data]);
  const equipamentos = useMemo(() => data?.listaEquipamentos || [], [data]);

  // Group usuarios by perfil
  const perfilGroups = useMemo(() => {
    const groups = {};
    usuarios.forEach((u) => {
      const perfil = u.perfil?.descricao || 'Outros';
      if (!groups[perfil]) groups[perfil] = [];
      groups[perfil].push(u);
    });
    return groups;
  }, [usuarios]);

  const perfis = Object.keys(perfilGroups);

  // Filter usuarios
  const filteredUsuarios = useMemo(() => {
    return usuarios.filter((u) => {
      const matchSearch =
        !search ||
        u.nome?.toLowerCase().includes(search.toLowerCase()) ||
        u.email?.toLowerCase().includes(search.toLowerCase()) ||
        u.cargo?.toLowerCase().includes(search.toLowerCase());
      const matchStatus =
        statusFilter === 'todos' ||
        (statusFilter === 'ativo' && u.ativo) ||
        (statusFilter === 'inativo' && !u.ativo);
      const matchPerfil = perfilFilter === 'todos' || u.perfil?.descricao === perfilFilter;
      return matchSearch && matchStatus && matchPerfil;
    });
  }, [usuarios, search, statusFilter, perfilFilter]);

  const filteredGroups = useMemo(() => {
    const groups = {};
    filteredUsuarios.forEach((u) => {
      const perfil = u.perfil?.descricao || 'Outros';
      if (!groups[perfil]) groups[perfil] = [];
      groups[perfil].push(u);
    });
    return groups;
  }, [filteredUsuarios]);

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    return (parts[0]?.[0] || '') + (parts[parts.length - 1]?.[0] || '');
  };

  const perfilColors = {
    Administrador: '#e65100',
    Personalizado: '#1565c0',
    'Cliente Obra': '#2e7d32',
  };

  return (
    <Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`Usuários (${usuarios.length})`} />
        <Tab
          label={`Mão de Obra (${(maoDeObra.personalizada?.length || 0) + (maoDeObra.padrao?.length || 0)})`}
        />
        <Tab label={`Equipamentos (${equipamentos.length})`} />
      </Tabs>

      {/* TAB 0: Usuários */}
      {tab === 0 && (
        <>
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <TextField
              placeholder="Pesquisa"
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 200, flexGrow: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Todos os status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Todos os status"
              >
                <MenuItem value="todos">Todos os status</MenuItem>
                <MenuItem value="ativo">Ativo</MenuItem>
                <MenuItem value="inativo">Inativo</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Todos os perfis</InputLabel>
              <Select
                value={perfilFilter}
                onChange={(e) => setPerfilFilter(e.target.value)}
                label="Todos os perfis"
              >
                <MenuItem value="todos">Todos os perfis</MenuItem>
                {perfis.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {Object.entries(filteredGroups).map(([perfil, users]) => (
            <Box key={perfil} sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, color: perfilColors[perfil] || '#333', mb: 1 }}
              >
                {perfil === 'Administrador'
                  ? 'Administradores'
                  : perfil === 'Personalizado'
                    ? 'Personalizados'
                    : perfil}{' '}
                ({users.length})
              </Typography>
              <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                {users.map((u, idx) => (
                  <Box key={u._id || idx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, gap: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: perfilColors[perfil] || '#999',
                          width: 36,
                          height: 36,
                          fontSize: 14,
                          fontWeight: 700,
                        }}
                      >
                        {getInitials(u.nome)}
                      </Avatar>
                      <Typography sx={{ fontWeight: 600, minWidth: 160 }}>{u.nome}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                        {u.email}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 180 }}>
                        {u.cargo}
                      </Typography>
                      <Chip
                        label={u.ativo ? 'Ativo' : 'Inativo'}
                        size="small"
                        sx={{
                          bgcolor: u.ativo ? '#4caf50' : '#9e9e9e',
                          color: '#fff',
                          fontWeight: 600,
                          minWidth: 60,
                        }}
                      />
                    </Box>
                    {idx < users.length - 1 && <Divider />}
                  </Box>
                ))}
              </Paper>
            </Box>
          ))}
        </>
      )}

      {/* TAB 1: Mão de Obra */}
      {tab === 1 && (
        <>
          {maoDeObra.personalizada?.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1565c0', mb: 1 }}>
                Personalizada ({maoDeObra.personalizada.length})
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Nome</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Função</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Categoria</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Horário</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Presença</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {maoDeObra.personalizada.map((m, i) => (
                      <TableRow key={m._id || i}>
                        <TableCell>{m.nome}</TableCell>
                        <TableCell>{m.funcao}</TableCell>
                        <TableCell>{m.categoria?.descricao}</TableCell>
                        <TableCell>
                          {m.horaInicio && m.horaFim ? `${m.horaInicio} - ${m.horaFim}` : '—'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={m.presenca ? 'Sim' : 'Não'}
                            size="small"
                            color={m.presenca ? 'success' : 'default'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
          {maoDeObra.padrao?.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#e65100', mb: 1 }}>
                Padrão ({maoDeObra.padrao.length})
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Descrição</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Quantidade</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Categoria</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {maoDeObra.padrao.map((m, i) => (
                      <TableRow key={m._id || i}>
                        <TableCell>{m.descricao}</TableCell>
                        <TableCell>{m.quantidade}</TableCell>
                        <TableCell>{m.categoria?.descricao}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </>
      )}

      {/* TAB 2: Equipamentos */}
      {tab === 2 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Descrição</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {equipamentos.map((eq, i) => (
                <TableRow key={eq._id || i}>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell>{eq.descricao}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

/* ─── Subcomponente: Obras (com navegação para detalhes) ─── */
function ObrasView({ data, token }) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [selectedObra, setSelectedObra] = useState(null);
  const [obraDetail, setObraDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [fotoDialog, setFotoDialog] = useState(null);

  const obras = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const statuses = useMemo(() => {
    const s = new Set();
    obras.forEach((o) => {
      if (o.status?.descricao) s.add(o.status.descricao);
    });
    return Array.from(s);
  }, [obras]);

  const filtered = useMemo(() => {
    return obras.filter((o) => {
      const matchSearch = !search || o.nome?.toLowerCase().includes(search.toLowerCase());
      const matchStatus = statusFilter === 'todos' || o.status?.descricao === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [obras, search, statusFilter]);

  const statusColor = (desc) => {
    switch (desc) {
      case 'Em Andamento':
        return '#1976d2';
      case 'Concluída':
        return '#2e7d32';
      case 'Não Iniciada':
        return '#ed6c02';
      default:
        return '#666';
    }
  };

  const handleObraClick = async (obra) => {
    setSelectedObra(obra);
    setLoadingDetail(true);
    setObraDetail(null);
    try {
      // Fetch obra detail + full relatórios list in parallel
      const [detailRes, relatoriosRes] = await Promise.all([
        fetch(`${API_BASE}/obras/${obra._id}`, {
          headers: { token: token, 'Content-Type': 'application/json' },
        }),
        fetch(`${API_BASE}/obras/${obra._id}/relatorios`, {
          headers: { token: token, 'Content-Type': 'application/json' },
        }),
      ]);

      let detail = null;
      let allRelatorios = [];

      if (detailRes.ok) detail = await detailRes.json();
      if (relatoriosRes.ok) allRelatorios = await relatoriosRes.json();

      if (detail) {
        // Fetch full detail of each relatório to get all photos/videos
        const relDetails = await Promise.all(
          allRelatorios.map((rel) =>
            fetch(`${API_BASE}/obras/${obra._id}/relatorios/${rel._id}`, {
              headers: { token: token, 'Content-Type': 'application/json' },
            })
              .then((r) => (r.ok ? r.json() : null))
              .catch(() => null)
          )
        );

        // Consolidate all photos and videos from all relatórios
        const allFotos = [];
        const allVideos = [];
        const fullRelatorios = [];

        for (const rd of relDetails) {
          if (!rd) continue;
          fullRelatorios.push(rd);
          const fotos = rd.galeriaDeFotos || [];
          const vids = rd.videos || [];
          fotos.forEach((f) =>
            allFotos.push({ ...f, relatorioNumero: rd.numero, relatorioData: rd.data })
          );
          vids.forEach((v) =>
            allVideos.push({ ...v, relatorioNumero: rd.numero, relatorioData: rd.data })
          );
        }

        detail._allRelatorios = fullRelatorios;
        detail._allFotos = allFotos;
        detail._allVideos = allVideos;
        setObraDetail(detail);
      }
    } catch (err) {
      console.error('Erro ao carregar detalhes da obra:', err);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleBack = () => {
    setSelectedObra(null);
    setObraDetail(null);
  };

  // ─── Visão de detalhes da obra ───
  if (selectedObra) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <IconButton onClick={handleBack} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {selectedObra.nome}
          </Typography>
          <Chip
            label={selectedObra.status?.descricao || 'N/A'}
            size="small"
            sx={{
              color: statusColor(selectedObra.status?.descricao),
              borderColor: statusColor(selectedObra.status?.descricao),
              fontWeight: 600,
            }}
            variant="outlined"
          />
        </Box>

        {loadingDetail ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : obraDetail ? (
          <ObraDetalhesContent detail={obraDetail} onFotoClick={setFotoDialog} />
        ) : (
          <Typography color="error">Não foi possível carregar os detalhes.</Typography>
        )}

        {/* Dialog de foto ampliada */}
        <Dialog open={!!fotoDialog} onClose={() => setFotoDialog(null)} maxWidth="lg">
          <DialogTitle
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5 }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {fotoDialog?.descricao || 'Foto'}
            </Typography>
            <IconButton size="small" onClick={() => setFotoDialog(null)}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0, textAlign: 'center', bgcolor: '#000' }}>
            {fotoDialog && (
              <Box
                component="img"
                src={fotoDialog.url}
                alt={fotoDialog.descricao || 'Foto'}
                sx={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
              />
            )}
          </DialogContent>
        </Dialog>
      </Box>
    );
  }

  // ─── Lista de obras ───
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Obras ({filtered.length})
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <TextField
          placeholder="Pesquisa"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Todas as obras</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            label="Todas as obras"
          >
            <MenuItem value="todos">Todas as obras</MenuItem>
            {statuses.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        {filtered.map((obra, idx) => (
          <Box key={obra._id || idx}>
            <Box
              onClick={() => handleObraClick(obra)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 2,
                py: 2,
                gap: 2,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              {obra.fotoUrl ? (
                <Avatar variant="rounded" src={obra.fotoUrl} sx={{ width: 48, height: 48 }} />
              ) : (
                <Avatar variant="rounded" sx={{ bgcolor: '#eceff1', width: 48, height: 48 }}>
                  <ConstructionIcon sx={{ color: '#607d8b' }} />
                </Avatar>
              )}
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>{obra.nome}</Typography>
                <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <FolderIcon sx={{ fontSize: 16, color: 'grey.500' }} />
                    <Typography variant="caption" color="text.secondary">
                      {obra.totalRelatorios || 0}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PhotoCameraIcon sx={{ fontSize: 16, color: 'grey.500' }} />
                    <Typography variant="caption" color="text.secondary">
                      {obra.totalFotos || 0}
                    </Typography>
                  </Box>
                  {obra.ultimoRelatorio && (
                    <Typography variant="caption" color="text.secondary">
                      Último relatório: {obra.ultimoRelatorio.data} (#{obra.ultimoRelatorio.numero})
                    </Typography>
                  )}
                </Box>
              </Box>
              <Chip
                label={obra.status?.descricao || 'N/A'}
                size="small"
                sx={{
                  color: statusColor(obra.status?.descricao),
                  borderColor: statusColor(obra.status?.descricao),
                  fontWeight: 600,
                  minWidth: 110,
                }}
                variant="outlined"
              />
            </Box>
            {idx < filtered.length - 1 && <Divider />}
          </Box>
        ))}
        {filtered.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">Nenhuma obra encontrada.</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
}

/* ─── Conteúdo de detalhes de uma obra (layout Diário de Obra) ─── */
function ObraDetalhesContent({ detail, onFotoClick }) {
  const [section, setSection] = useState('visao');
  const [showAllFotos, setShowAllFotos] = useState(false);
  const [showAllRelatorios, setShowAllRelatorios] = useState(false);
  const [selectedRelatorio, setSelectedRelatorio] = useState(null);
  const [pdfDialogUrl, setPdfDialogUrl] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const [pdfPreloaded, setPdfPreloaded] = useState(false);
  const pdfFrameRef = useRef(null);
  const pdfCacheRef = useRef({});
  const pdfAbortRef = useRef(null);

  // Função para buscar PDF via proxy (cache real no servidor)
  const fetchPdfViaProxy = (pdfUrl, signal, force = false) => {
    const jwtToken = localStorage.getItem('token');
    const proxyUrl = `/api/proxy_pdf?url=${encodeURIComponent(pdfUrl)}${force ? '&force=1' : ''}`;
    return fetch(proxyUrl, {
      headers: jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {},
      signal,
    })
      .then((resp) => {
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        return resp.blob();
      })
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        pdfCacheRef.current[pdfUrl] = blobUrl;
        return blobUrl;
      });
  };

  // Pré-carregar PDF quando seleciona relatório
  useEffect(() => {
    if (!selectedRelatorio?.linkPdf) {
      setPdfPreloaded(false);
      return;
    }
    const pdfUrl = selectedRelatorio.linkPdf;
    if (pdfCacheRef.current[pdfUrl]) {
      setPdfPreloaded(true);
      return;
    }
    setPdfPreloaded(false);
    const abortController = new AbortController();
    pdfAbortRef.current = abortController;
    fetchPdfViaProxy(pdfUrl, abortController.signal)
      .then(() => setPdfPreloaded(true))
      .catch((err) => {
        if (err.name !== 'AbortError') console.warn('Pré-carregamento PDF:', err.message);
      });
    return () => {
      abortController.abort();
    };
  }, [selectedRelatorio]);

  const totais = detail.visaoGeral?.total || {};
  const allRelatorios = detail._allRelatorios || detail.visaoGeral?.ultimosRelatorios || [];
  const allFotos = detail._allFotos || detail.visaoGeral?.ultimasFotos || [];
  const allVideos = detail._allVideos || detail.visaoGeral?.ultimosVideos || [];

  // For "Visão Geral" show recent items; "Ver tudo" shows all
  const recenteFotos = showAllFotos ? allFotos : allFotos.slice(0, 8);
  const recenteRelatorios = showAllRelatorios ? allRelatorios : allRelatorios.slice(0, 5);

  const statusColor = (desc) => {
    if (!desc) return '#666';
    const d = desc.toLowerCase();
    if (d.includes('andamento') || d.includes('preenchendo')) return '#1976d2';
    if (d.includes('conclu') || d.includes('finaliz') || d.includes('revisad')) return '#2e7d32';
    if (d.includes('revis')) return '#ed6c02';
    if (d.includes('não inic')) return '#9e9e9e';
    return '#666';
  };

  const prazoPercent = detail.prazo
    ? Math.min(
        100,
        Math.round((detail.prazo.decorrido / Math.max(detail.prazo.contratual, 1)) * 100)
      )
    : 0;

  const handleClosePdfDialog = () => {
    setPdfDialogUrl('');
    setPdfError('');
    setPdfLoading(false);
  };

  const handleOpenPdf = (pdfUrl) => {
    if (!pdfUrl) return;
    setPdfError('');
    // Já em cache? Mostrar direto
    if (pdfCacheRef.current[pdfUrl]) {
      setPdfLoading(false);
      setPdfDialogUrl(pdfCacheRef.current[pdfUrl]);
      return;
    }
    // Ainda não carregou — buscar via proxy
    setPdfLoading(true);
    setPdfDialogUrl('loading');
    fetchPdfViaProxy(pdfUrl)
      .then((blobUrl) => {
        setPdfPreloaded(true);
        setPdfLoading(false);
        setPdfDialogUrl(blobUrl);
      })
      .catch((err) => {
        setPdfLoading(false);
        setPdfError(`Falha ao carregar PDF: ${err.message}`);
        setPdfDialogUrl('');
      });
  };

  // Recarregar PDF forçando nova geração (corrige fotos quebradas)
  const handleReloadPdf = () => {
    const pdfUrl = selectedRelatorio?.linkPdf;
    if (!pdfUrl) return;
    // Revogar blob antigo se existir
    if (pdfCacheRef.current[pdfUrl]) {
      URL.revokeObjectURL(pdfCacheRef.current[pdfUrl]);
      delete pdfCacheRef.current[pdfUrl];
    }
    setPdfLoading(true);
    setPdfDialogUrl('loading');
    setPdfPreloaded(false);
    fetchPdfViaProxy(pdfUrl, undefined, true)
      .then((blobUrl) => {
        setPdfPreloaded(true);
        setPdfLoading(false);
        setPdfDialogUrl(blobUrl);
      })
      .catch((err) => {
        setPdfLoading(false);
        setPdfError(`Falha ao recarregar PDF: ${err.message}`);
        setPdfDialogUrl('');
      });
  };

  const handlePrintPdf = () => {
    const frameWindow = pdfFrameRef.current?.contentWindow;
    if (frameWindow) {
      frameWindow.focus();
      frameWindow.print();
      return;
    }
    if (selectedRelatorio?.linkPdf) {
      window.open(selectedRelatorio.linkPdf, '_blank', 'noopener,noreferrer');
    }
  };

  const climaEntries = (() => {
    const clima = selectedRelatorio?.clima;
    if (!clima || typeof clima === 'string') return [];

    const labelMap = {
      manha: 'Manhã',
      manhã: 'Manhã',
      tarde: 'Tarde',
      noite: 'Noite',
    };

    return Object.entries(clima)
      .filter(([, value]) => value && typeof value === 'object' && value.ativo !== false)
      .map(([key, value]) => ({
        periodo: labelMap[key.toLowerCase()] || key,
        tempo: value.clima || value.tempo || '—',
        condicao: value.condicao || value.condição || '—',
      }));
  })();

  const getClimaIcon = (tempo) => {
    const descricao = String(tempo || '').toLowerCase();
    if (descricao.includes('trovo') || descricao.includes('tempest'))
      return <ThunderstormOutlinedIcon sx={{ fontSize: 18, color: '#616161' }} />;
    if (descricao.includes('chuva') || descricao.includes('garoa'))
      return <GrainOutlinedIcon sx={{ fontSize: 18, color: '#616161' }} />;
    if (descricao.includes('nublado') || descricao.includes('nuvem'))
      return <CloudQueueOutlinedIcon sx={{ fontSize: 18, color: '#616161' }} />;
    return <WbSunnyOutlinedIcon sx={{ fontSize: 18, color: '#616161' }} />;
  };

  const sidebarItems = [
    { key: 'visao', label: 'Visão geral', icon: <VisibilityIcon fontSize="small" /> },
    {
      key: 'relatorios',
      label: 'Relatórios',
      icon: <DescriptionIcon fontSize="small" />,
      badge: totais.relatorios,
    },
    {
      key: 'atividades',
      label: 'Atividades',
      icon: <FormatListBulletedIcon fontSize="small" />,
      badge: totais.atividades,
    },
    {
      key: 'ocorrencias',
      label: 'Ocorrências',
      icon: <WarningAmberIcon fontSize="small" />,
      badge: totais.ocorrencias,
    },
    {
      key: 'comentarios',
      label: 'Comentários',
      icon: <ChatBubbleOutlineIcon fontSize="small" />,
      badge: totais.comentarios,
    },
    {
      key: 'fotos',
      label: 'Fotos',
      icon: <PhotoCameraIcon fontSize="small" />,
      badge: totais.fotos,
    },
    {
      key: 'videos',
      label: 'Vídeos',
      icon: <VideocamIcon fontSize="small" />,
      badge: totais.videos,
    },
    { key: 'filtro', label: 'Filtro de busca', icon: <FilterListIcon fontSize="small" /> },
  ];

  /* ─── Stats Row (topo) ─── */
  const statsItems = [
    {
      label: 'Relatórios',
      value: totais.relatorios || 0,
      icon: <DescriptionIcon />,
      color: '#e65100',
      section: 'relatorios',
    },
    {
      label: 'Atividades',
      value: totais.atividades || 0,
      icon: <FormatListBulletedIcon />,
      color: '#e65100',
      section: 'atividades',
    },
    {
      label: 'Ocorrências',
      value: totais.ocorrencias || 0,
      icon: <WarningAmberIcon />,
      color: '#e65100',
      section: 'ocorrencias',
    },
    {
      label: 'Comentários',
      value: totais.comentarios || 0,
      icon: <ChatBubbleOutlineIcon />,
      color: '#e65100',
      section: 'comentarios',
    },
    {
      label: 'Fotos',
      value: totais.fotos || 0,
      icon: <PhotoCameraIcon />,
      color: '#e65100',
      section: 'fotos',
    },
    {
      label: 'Vídeos',
      value: totais.videos || 0,
      icon: <VideocamIcon />,
      color: '#e65100',
      section: 'videos',
    },
  ];

  return (
    <Box>
      {/* ─── Stats Row ─── */}
      <Grid container spacing={1.5} sx={{ mb: 3 }}>
        {statsItems.map((s) => (
          <Grid item xs={4} sm={2} key={s.label}>
            <Paper
              variant="outlined"
              onClick={() => setSection(s.section)}
              sx={{
                p: 1.5,
                textAlign: 'center',
                borderRadius: 2,
                borderColor: '#e0e0e0',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: '#e65100',
                  bgcolor: '#fff3e0',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                },
                ...(section === s.section && { borderColor: '#e65100', bgcolor: '#fff3e0' }),
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 700, color: s.color }}>
                {s.value}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                  mt: 0.5,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {s.label}
                </Typography>
                <Box sx={{ color: 'grey.400' }}>
                  {React.cloneElement(s.icon, { sx: { fontSize: 16 } })}
                </Box>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* ─── Layout principal: Sidebar + Content ─── */}
      <Grid container spacing={2}>
        {/* Sidebar */}
        <Grid item xs={12} sm={3} md={2.5}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            {detail.fotoUrl ? (
              <Box
                component="img"
                src={detail.fotoUrl}
                alt={detail.nome}
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: 2,
                  objectFit: 'cover',
                  mx: 'auto',
                  display: 'block',
                }}
              />
            ) : (
              <Avatar
                variant="rounded"
                sx={{ width: 80, height: 80, mx: 'auto', bgcolor: '#eceff1' }}
              >
                <ConstructionIcon sx={{ fontSize: 40, color: '#607d8b' }} />
              </Avatar>
            )}
          </Box>
          <List dense disablePadding>
            {sidebarItems.map((item) => (
              <ListItem key={item.key} disablePadding>
                <ListItemButton
                  selected={section === item.key}
                  onClick={() => setSection(item.key)}
                  sx={{ borderRadius: 1, py: 0.8 }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontSize: '0.85rem' }}
                  />
                  {item.badge > 0 && (
                    <Chip
                      label={item.badge}
                      size="small"
                      sx={{ height: 20, fontSize: '0.7rem', bgcolor: '#e3f2fd' }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Grid>

        {/* Main content */}
        <Grid item xs={12} sm={9} md={9.5}>
          {selectedRelatorio ? (
            <Box>
              {/* Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <IconButton size="small" onClick={() => setSelectedRelatorio(null)}>
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Typography sx={{ fontWeight: 700, color: '#e65100', fontSize: '1.1rem' }}>
                  Relatório #{selectedRelatorio.numero}
                </Typography>
                <Chip
                  label={selectedRelatorio.status?.descricao || 'N/A'}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    bgcolor: statusColor(selectedRelatorio.status?.descricao),
                    color: '#fff',
                  }}
                />
              </Box>

              {/* Info cards */}
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={6} sm={3}>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Data
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {selectedRelatorio.data || '—'}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Dia
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {selectedRelatorio.diaDaSemana || '—'}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Modelo
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                      {selectedRelatorio.modeloDeRelatorio?.descricao ||
                        selectedRelatorio.modeloDeRelatorioGlobal?.descricao ||
                        '—'}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  {selectedRelatorio.linkPdf && (
                    <Paper
                      variant="outlined"
                      onClick={() => handleOpenPdf(selectedRelatorio.linkPdf)}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        display: 'block',
                        textDecoration: 'none',
                        color: 'inherit',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: '#fff3e0', borderColor: '#e65100' },
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        PDF
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <DescriptionIcon
                          sx={{ fontSize: 16, color: pdfPreloaded ? '#2e7d32' : '#e65100' }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, color: pdfPreloaded ? '#2e7d32' : '#e65100' }}
                        >
                          {pdfPreloaded ? 'PDF Pronto' : 'Abrir PDF'}
                        </Typography>
                      </Box>
                    </Paper>
                  )}
                </Grid>
              </Grid>

              {/* Stats mini */}
              <Grid container spacing={1} sx={{ mb: 2 }}>
                {[
                  {
                    label: 'Fotos',
                    count: selectedRelatorio.galeriaDeFotos?.length || 0,
                    icon: <PhotoCameraIcon sx={{ fontSize: 16 }} />,
                  },
                  {
                    label: 'Vídeos',
                    count: selectedRelatorio.videos?.length || 0,
                    icon: <VideocamIcon sx={{ fontSize: 16 }} />,
                  },
                  {
                    label: 'Atividades',
                    count: selectedRelatorio.atividades?.length || 0,
                    icon: <FormatListBulletedIcon sx={{ fontSize: 16 }} />,
                  },
                  {
                    label: 'Ocorrências',
                    count: selectedRelatorio.ocorrencias?.length || 0,
                    icon: <WarningAmberIcon sx={{ fontSize: 16 }} />,
                  },
                  {
                    label: 'Comentários',
                    count: selectedRelatorio.comentarios?.length || 0,
                    icon: <ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />,
                  },
                ].map((s, i) => (
                  <Grid item xs={4} sm={2.4} key={i}>
                    <Paper variant="outlined" sx={{ p: 1, borderRadius: 2, textAlign: 'center' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 0.5 /*, color: s.count > 0 ? '#e65100' : 'grey.400'*/,
                        }}
                      >
                        {React.cloneElement(s.icon, {
                          sx: { fontSize: 16, color: s.count > 0 ? '#e65100' : 'grey.400' },
                        })}
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: '1rem',
                            color: s.count > 0 ? '#e65100' : 'grey.400',
                          }}
                        >
                          {s.count}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {s.label}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              {/* Galeria de Fotos */}
              {selectedRelatorio.galeriaDeFotos?.length > 0 && (
                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', mb: 2 }}>
                  <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #eee' }}>
                    <Typography sx={{ fontWeight: 700, color: '#e65100' }}>
                      Fotos ({selectedRelatorio.galeriaDeFotos.length})
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1.5 }}>
                    <Grid container spacing={1}>
                      {selectedRelatorio.galeriaDeFotos.map((foto, i) => (
                        <Grid item xs={6} sm={4} md={3} key={i}>
                          <Paper
                            onClick={() => onFotoClick && onFotoClick(foto)}
                            elevation={0}
                            sx={{
                              cursor: 'pointer',
                              borderRadius: 2,
                              overflow: 'hidden',
                              border: '1px solid #eee',
                              '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.12)' },
                            }}
                          >
                            <Box
                              component="img"
                              src={foto.urlMiniatura || foto.url}
                              alt={foto.descricao || `Foto ${i + 1}`}
                              sx={{
                                width: '100%',
                                height: 150,
                                objectFit: 'cover',
                                display: 'block',
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                            {foto.descricao && (
                              <Box sx={{ px: 1, py: 0.5 }}>
                                <Typography variant="caption" noWrap>
                                  {foto.descricao}
                                </Typography>
                              </Box>
                            )}
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Paper>
              )}

              {/* Vídeos */}
              {selectedRelatorio.videos?.length > 0 && (
                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', mb: 2 }}>
                  <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #eee' }}>
                    <Typography sx={{ fontWeight: 700, color: '#e65100' }}>
                      Vídeos ({selectedRelatorio.videos.length})
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1.5 }}>
                    <Grid container spacing={1}>
                      {selectedRelatorio.videos.map((video, i) => (
                        <Grid item xs={6} sm={4} md={3} key={i}>
                          <Paper
                            elevation={0}
                            sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #eee' }}
                          >
                            <Box
                              component="a"
                              href={video.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ display: 'block', position: 'relative' }}
                            >
                              {video.urlFoto ? (
                                <Box
                                  component="img"
                                  src={video.urlFoto}
                                  alt={video.descricao || `Vídeo ${i + 1}`}
                                  sx={{
                                    width: '100%',
                                    height: 140,
                                    objectFit: 'cover',
                                    display: 'block',
                                  }}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    height: 140,
                                    bgcolor: '#f5f5f5',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <VideocamIcon sx={{ fontSize: 48, color: 'grey.300' }} />
                                </Box>
                              )}
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  bgcolor: 'rgba(0,0,0,0.55)',
                                  borderRadius: '50%',
                                  width: 44,
                                  height: 44,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <VideocamIcon sx={{ color: '#fff', fontSize: 22 }} />
                              </Box>
                            </Box>
                            <Box sx={{ px: 1, py: 0.5 }}>
                              <Typography variant="caption" noWrap>
                                {video.descricao || `Vídeo ${i + 1}`}
                              </Typography>
                              {video.duracao && (
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                  {video.duracao}
                                </Typography>
                              )}
                            </Box>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Paper>
              )}

              {/* Atividades */}
              {selectedRelatorio.atividades?.length > 0 && (
                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', mb: 2 }}>
                  <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #eee' }}>
                    <Typography sx={{ fontWeight: 700, color: '#e65100' }}>
                      Atividades ({selectedRelatorio.atividades.length})
                    </Typography>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        {selectedRelatorio.atividades.map((atv, j) => (
                          <TableRow key={j} hover>
                            <TableCell sx={{ fontSize: '0.8rem' }}>
                              {atv.descricao ||
                                atv.titulo ||
                                atv.servico?.descricao ||
                                JSON.stringify(atv).slice(0, 150)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}

              {/* Ocorrências */}
              {selectedRelatorio.ocorrencias?.length > 0 && (
                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', mb: 2 }}>
                  <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #eee' }}>
                    <Typography sx={{ fontWeight: 700, color: '#e65100' }}>
                      Ocorrências ({selectedRelatorio.ocorrencias.length})
                    </Typography>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        {selectedRelatorio.ocorrencias.map((oc, j) => (
                          <TableRow key={j} hover>
                            <TableCell sx={{ fontSize: '0.8rem' }}>
                              {oc.descricao || oc.titulo || JSON.stringify(oc).slice(0, 150)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}

              {/* Comentários */}
              {selectedRelatorio.comentarios?.length > 0 && (
                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', mb: 2 }}>
                  <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #eee' }}>
                    <Typography sx={{ fontWeight: 700, color: '#e65100' }}>
                      Comentários ({selectedRelatorio.comentarios.length})
                    </Typography>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        {selectedRelatorio.comentarios.map((cm, j) => (
                          <TableRow key={j} hover>
                            <TableCell sx={{ fontSize: '0.8rem' }}>
                              {cm.descricao ||
                                cm.texto ||
                                cm.comentario ||
                                JSON.stringify(cm).slice(0, 150)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}

              {/* Clima / Mão de Obra / Equipamentos */}
              <Grid container spacing={2}>
                {selectedRelatorio.clima && (
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.95rem', width: '30%' }}>
                              Clima
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.95rem', width: '35%' }}>
                              Tempo
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.95rem', width: '35%' }}>
                              Condição
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {climaEntries.length > 0 ? (
                            climaEntries.map((item) => (
                              <TableRow key={item.periodo} hover>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                                  {item.periodo}
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.9rem' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {getClimaIcon(item.tempo)}
                                    <Typography variant="body2">{item.tempo}</Typography>
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.9rem' }}>{item.condicao}</TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={3} sx={{ py: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                  {typeof selectedRelatorio.clima === 'string'
                                    ? selectedRelatorio.clima
                                    : selectedRelatorio.clima.descricao ||
                                      'Informações de clima indisponíveis.'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </Paper>
                  </Grid>
                )}
                {selectedRelatorio.maoDeObra && selectedRelatorio.maoDeObra.length > 0 && (
                  <Grid item xs={12} sm={6}>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Mão de Obra ({selectedRelatorio.maoDeObra.length})
                      </Typography>
                      {selectedRelatorio.maoDeObra.slice(0, 5).map((m, i) => (
                        <Typography key={i} variant="body2" sx={{ fontSize: '0.8rem', mt: 0.3 }}>
                          {m.funcao?.descricao || m.descricao || JSON.stringify(m).slice(0, 60)} —
                          Qtd: {m.quantidade || m.qtd || '—'}
                        </Typography>
                      ))}
                      {selectedRelatorio.maoDeObra.length > 5 && (
                        <Typography variant="caption" color="text.secondary">
                          ...e mais {selectedRelatorio.maoDeObra.length - 5}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                )}
                {selectedRelatorio.equipamentos && selectedRelatorio.equipamentos.length > 0 && (
                  <Grid item xs={12} sm={6}>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Equipamentos ({selectedRelatorio.equipamentos.length})
                      </Typography>
                      {selectedRelatorio.equipamentos.slice(0, 5).map((eq, i) => (
                        <Typography key={i} variant="body2" sx={{ fontSize: '0.8rem', mt: 0.3 }}>
                          {eq.equipamento?.descricao ||
                            eq.descricao ||
                            JSON.stringify(eq).slice(0, 60)}{' '}
                          — Qtd: {eq.quantidade || eq.qtd || '—'}
                        </Typography>
                      ))}
                      {selectedRelatorio.equipamentos.length > 5 && (
                        <Typography variant="caption" color="text.secondary">
                          ...e mais {selectedRelatorio.equipamentos.length - 5}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>
          ) : (
            <>
              {/* ─── VISÃO GERAL ─── */}
              {section === 'visao' && (
                <Box>
                  {/* Relatórios recentes + Fotos recentes (2 colunas) */}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {/* Relatórios recentes */}
                    <Grid item xs={12} md={6}>
                      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            px: 2,
                            py: 1.5,
                            borderBottom: '1px solid #eee',
                          }}
                        >
                          <Typography sx={{ fontWeight: 700, color: '#e65100' }}>
                            Relatórios recentes
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#1976d2',
                              cursor: 'pointer',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                            onClick={() => setShowAllRelatorios(!showAllRelatorios)}
                          >
                            {showAllRelatorios ? 'Ver menos' : 'Ver tudo'}
                          </Typography>
                        </Box>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                                  Data
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                                  Nº
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                                  Status
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                                  Modelo de relatório
                                </TableCell>
                                <TableCell
                                  sx={{ fontWeight: 700, fontSize: '0.8rem' }}
                                  align="center"
                                >
                                  <PhotoCameraIcon sx={{ fontSize: 14 }} />
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {recenteRelatorios.length > 0 ? (
                                recenteRelatorios.map((rel, i) => (
                                  <TableRow key={rel._id || i} hover>
                                    <TableCell
                                      sx={{
                                        color: '#1976d2',
                                        fontSize: '0.8rem',
                                        cursor: 'pointer',
                                        '&:hover': { textDecoration: 'underline' },
                                      }}
                                      onClick={() => setSelectedRelatorio(rel)}
                                    >
                                      {rel.data}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        color: '#1976d2',
                                        fontSize: '0.8rem',
                                        cursor: 'pointer',
                                        fontWeight: 700,
                                        '&:hover': { textDecoration: 'underline' },
                                      }}
                                      onClick={() => setSelectedRelatorio(rel)}
                                    >
                                      {rel.numero}
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={rel.status?.descricao || 'N/A'}
                                        size="small"
                                        sx={{
                                          fontWeight: 600,
                                          fontSize: '0.7rem',
                                          bgcolor: statusColor(rel.status?.descricao),
                                          color: '#fff',
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.8rem' }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <DescriptionIcon sx={{ fontSize: 14, color: 'grey.500' }} />
                                        {rel.modeloDeRelatorio?.descricao ||
                                          rel.modeloDeRelatorioGlobal?.descricao ||
                                          '—'}
                                      </Box>
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontSize: '0.8rem' }}>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          gap: 0.3,
                                        }}
                                      >
                                        <PhotoCameraIcon sx={{ fontSize: 13, color: 'grey.500' }} />
                                        {rel.totalFotos ?? (rel.galeriaDeFotos?.length || 0)}
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                ))
                              ) : (
                                <TableRow>
                                  <TableCell colSpan={5} sx={{ textAlign: 'center', py: 3 }}>
                                    <Typography variant="body2" color="text.secondary">
                                      Nenhum relatório.
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Paper>
                    </Grid>

                    {/* Fotos recentes */}
                    <Grid item xs={12} md={6}>
                      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            px: 2,
                            py: 1.5,
                            borderBottom: '1px solid #eee',
                          }}
                        >
                          <Typography sx={{ fontWeight: 700, color: '#e65100' }}>
                            Fotos recentes
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#1976d2',
                              cursor: 'pointer',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                            onClick={() => setShowAllFotos(!showAllFotos)}
                          >
                            {showAllFotos ? 'Ver menos' : 'Ver tudo'}
                          </Typography>
                        </Box>
                        <Box sx={{ p: 1 }}>
                          <Grid container spacing={0.5}>
                            {recenteFotos.length > 0 ? (
                              recenteFotos.map((foto, i) => (
                                <Grid item xs={3} key={i}>
                                  <Box
                                    onClick={() => onFotoClick && onFotoClick(foto)}
                                    sx={{
                                      cursor: 'pointer',
                                      position: 'relative',
                                      borderRadius: 1,
                                      overflow: 'hidden',
                                      '&:hover': { opacity: 0.85 },
                                    }}
                                  >
                                    <Box
                                      component="img"
                                      src={foto.urlMiniatura || foto.url}
                                      alt={foto.descricao || `Foto ${i + 1}`}
                                      sx={{
                                        width: '100%',
                                        height: 90,
                                        objectFit: 'cover',
                                        display: 'block',
                                        borderRadius: 1,
                                      }}
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                      }}
                                    />
                                    {foto.descricao && (
                                      <Box
                                        sx={{
                                          position: 'absolute',
                                          bottom: 0,
                                          left: 0,
                                          right: 0,
                                          bgcolor: 'rgba(0,0,0,0.5)',
                                          px: 0.5,
                                          py: 0.3,
                                        }}
                                      >
                                        <Typography
                                          variant="caption"
                                          sx={{ color: '#fff', fontSize: '0.6rem' }}
                                          noWrap
                                        >
                                          {foto.descricao}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                </Grid>
                              ))
                            ) : (
                              <Grid item xs={12}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ textAlign: 'center', py: 3 }}
                                >
                                  Nenhuma foto.
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>

                  {/* ─── Informações da obra ─── */}
                  <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5 }}>
                    <Typography sx={{ fontWeight: 700, color: '#e65100', mb: 2 }}>
                      Informações da obra
                    </Typography>
                    <Grid container spacing={2}>
                      {/* Status + Nº contrato + Prazo barra */}
                      <Grid item xs={12} sm={4}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 600 }}
                        >
                          Status
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Chip
                            label={detail.status?.descricao || 'N/A'}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              bgcolor: statusColor(detail.status?.descricao),
                              color: '#fff',
                            }}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 600 }}
                        >
                          Nº do contrato
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {detail.numeroContrato || '—'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 600 }}
                        >
                          Prazo decorrido
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <LinearProgress
                            variant="determinate"
                            value={prazoPercent}
                            sx={{
                              height: 22,
                              borderRadius: 2,
                              bgcolor: '#e0e0e0',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: prazoPercent >= 100 ? '#1976d2' : '#42a5f5',
                                borderRadius: 2,
                              },
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              position: 'relative',
                              top: -19,
                              display: 'block',
                              textAlign: 'center',
                              color: '#fff',
                              fontWeight: 700,
                              fontSize: '0.75rem',
                            }}
                          >
                            {prazoPercent} %
                          </Typography>
                        </Box>
                      </Grid>

                      {/* Endereço + Prazos detalhados */}
                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 600 }}
                        >
                          Endereço
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {detail.endereco || '—'}
                        </Typography>
                      </Grid>
                      {detail.prazo && (
                        <>
                          <Grid item xs={4} sm={2}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontWeight: 600 }}
                            >
                              Prazo contratual
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.5 }}>
                              {detail.prazo.contratual} dias
                            </Typography>
                          </Grid>
                          <Grid item xs={4} sm={2}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontWeight: 600 }}
                            >
                              Prazo decorrido
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.5 }}>
                              {detail.prazo.decorrido} dias
                            </Typography>
                          </Grid>
                          <Grid item xs={4} sm={2}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontWeight: 600 }}
                            >
                              Prazo a vencer
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.5 }}>
                              {detail.prazo.aVencer} dias
                            </Typography>
                          </Grid>
                        </>
                      )}

                      {/* Responsável + Contratante + Datas */}
                      <Grid item xs={12} sm={3}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 600 }}
                        >
                          Responsável
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {detail.responsavel || '—'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 600 }}
                        >
                          Contratante
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {detail.cliente || '—'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 600 }}
                        >
                          Data início
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {detail.dataInicio || '—'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 600 }}
                        >
                          Previsão de término
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {detail.dataFim || '—'}
                        </Typography>
                      </Grid>

                      {/* Grupo + Observação */}
                      {detail.grupo?.descricao && (
                        <Grid item xs={12} sm={6}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 600 }}
                          >
                            Grupo
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {detail.grupo.descricao}
                          </Typography>
                        </Grid>
                      )}
                      {detail.observacao && (
                        <Grid item xs={12}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 600 }}
                          >
                            Observação
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {detail.observacao}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                </Box>
              )}

              {/* ─── RELATÓRIOS (todos) ─── */}
              {section === 'relatorios' && (
                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                  <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #eee' }}>
                    <Typography sx={{ fontWeight: 700, color: '#e65100' }}>
                      Todos os Relatórios ({allRelatorios.length})
                    </Typography>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Data</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Nº</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Dia</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Modelo</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }} align="center">
                            Fotos
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }} align="center">
                            Vídeos
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {allRelatorios.map((rel, i) => (
                          <TableRow key={rel._id || i} hover>
                            <TableCell
                              sx={{
                                color: '#1976d2',
                                fontSize: '0.8rem',
                                cursor: 'pointer',
                                '&:hover': { textDecoration: 'underline' },
                              }}
                              onClick={() => setSelectedRelatorio(rel)}
                            >
                              {rel.data}
                            </TableCell>
                            <TableCell
                              sx={{
                                color: '#1976d2',
                                fontSize: '0.8rem',
                                cursor: 'pointer',
                                fontWeight: 700,
                                '&:hover': { textDecoration: 'underline' },
                              }}
                              onClick={() => setSelectedRelatorio(rel)}
                            >
                              {rel.numero}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>
                              {rel.diaDaSemana || '—'}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={rel.status?.descricao || 'N/A'}
                                size="small"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                  bgcolor: statusColor(rel.status?.descricao),
                                  color: '#fff',
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>
                              {rel.modeloDeRelatorio?.descricao ||
                                rel.modeloDeRelatorioGlobal?.descricao ||
                                '—'}
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: '0.8rem' }}>
                              {rel.galeriaDeFotos?.length ?? rel.totalFotos ?? 0}
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: '0.8rem' }}>
                              {rel.videos?.length ?? rel.totalVideos ?? 0}
                            </TableCell>
                          </TableRow>
                        ))}
                        {allRelatorios.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={7} sx={{ textAlign: 'center', py: 3 }}>
                              <Typography variant="body2" color="text.secondary">
                                Nenhum relatório encontrado.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}

              {/* ─── FOTOS (todas) ─── */}
              {section === 'fotos' && (
                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                  <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #eee' }}>
                    <Typography sx={{ fontWeight: 700, color: '#e65100' }}>
                      Todas as Fotos ({allFotos.length})
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1.5 }}>
                    <Grid container spacing={1}>
                      {allFotos.length > 0 ? (
                        allFotos.map((foto, i) => (
                          <Grid item xs={6} sm={4} md={3} key={i}>
                            <Paper
                              onClick={() => onFotoClick && onFotoClick(foto)}
                              elevation={0}
                              sx={{
                                cursor: 'pointer',
                                borderRadius: 2,
                                overflow: 'hidden',
                                border: '1px solid #eee',
                                '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.12)' },
                              }}
                            >
                              <Box
                                component="img"
                                src={foto.urlMiniatura || foto.url}
                                alt={foto.descricao || `Foto ${i + 1}`}
                                sx={{
                                  width: '100%',
                                  height: 140,
                                  objectFit: 'cover',
                                  display: 'block',
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                              <Box sx={{ px: 1, py: 0.5 }}>
                                {foto.descricao && (
                                  <Typography variant="caption" noWrap sx={{ display: 'block' }}>
                                    {foto.descricao}
                                  </Typography>
                                )}
                                {foto.relatorioNumero && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontSize: '0.65rem' }}
                                  >
                                    Relatório #{foto.relatorioNumero} — {foto.relatorioData}
                                  </Typography>
                                )}
                              </Box>
                            </Paper>
                          </Grid>
                        ))
                      ) : (
                        <Grid item xs={12}>
                          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                            Nenhuma foto encontrada.
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </Paper>
              )}

              {/* ─── VÍDEOS (todos) ─── */}
              {section === 'videos' && (
                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                  <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #eee' }}>
                    <Typography sx={{ fontWeight: 700, color: '#e65100' }}>
                      Todos os Vídeos ({allVideos.length})
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1.5 }}>
                    <Grid container spacing={1}>
                      {allVideos.length > 0 ? (
                        allVideos.map((video, i) => (
                          <Grid item xs={6} sm={4} md={3} key={i}>
                            <Paper
                              elevation={0}
                              sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #eee' }}
                            >
                              <Box
                                component="a"
                                href={video.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ display: 'block', position: 'relative' }}
                              >
                                {video.urlFoto ? (
                                  <Box
                                    component="img"
                                    src={video.urlFoto}
                                    alt={video.descricao || `Vídeo ${i + 1}`}
                                    sx={{
                                      width: '100%',
                                      height: 140,
                                      objectFit: 'cover',
                                      display: 'block',
                                    }}
                                  />
                                ) : (
                                  <Box
                                    sx={{
                                      height: 140,
                                      bgcolor: '#f5f5f5',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    <VideocamIcon sx={{ fontSize: 48, color: 'grey.300' }} />
                                  </Box>
                                )}
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    bgcolor: 'rgba(0,0,0,0.55)',
                                    borderRadius: '50%',
                                    width: 44,
                                    height: 44,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <VideocamIcon sx={{ color: '#fff', fontSize: 22 }} />
                                </Box>
                              </Box>
                              <Box
                                sx={{
                                  px: 1,
                                  py: 0.5,
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                }}
                              >
                                <Typography variant="caption" noWrap>
                                  {video.descricao || `Vídeo ${i + 1}`}
                                </Typography>
                                {video.duracao && (
                                  <Typography variant="caption" color="text.secondary">
                                    {video.duracao}
                                  </Typography>
                                )}
                              </Box>
                              {video.relatorioNumero && (
                                <Box sx={{ px: 1, pb: 0.5 }}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontSize: '0.65rem' }}
                                  >
                                    Relatório #{video.relatorioNumero}
                                  </Typography>
                                </Box>
                              )}
                            </Paper>
                          </Grid>
                        ))
                      ) : (
                        <Grid item xs={12}>
                          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                            Nenhum vídeo encontrado.
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </Paper>
              )}

              {/* ─── ATIVIDADES (todas) ─── */}
              {section === 'atividades' && (
                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                  <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #eee' }}>
                    <Typography sx={{ fontWeight: 700, color: '#e65100' }}>
                      Atividades ({totais.atividades || 0})
                    </Typography>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                            Relatório
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Data</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                            Descrição
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {allRelatorios.filter((r) => r.atividades?.length > 0).length > 0 ? (
                          allRelatorios
                            .filter((r) => r.atividades?.length > 0)
                            .flatMap((rel) =>
                              rel.atividades.map((atv, j) => (
                                <TableRow key={`${rel._id}-atv-${j}`} hover>
                                  <TableCell sx={{ fontSize: '0.8rem', color: '#1976d2' }}>
                                    #{rel.numero}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.8rem' }}>{rel.data}</TableCell>
                                  <TableCell sx={{ fontSize: '0.8rem' }}>
                                    {atv.descricao ||
                                      atv.titulo ||
                                      JSON.stringify(atv).slice(0, 120)}
                                  </TableCell>
                                </TableRow>
                              ))
                            )
                        ) : (
                          <TableRow>
                            <TableCell colSpan={3} sx={{ textAlign: 'center', py: 3 }}>
                              <Typography variant="body2" color="text.secondary">
                                Nenhuma atividade encontrada.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}

              {/* ─── OCORRÊNCIAS (todas) ─── */}
              {section === 'ocorrencias' && (
                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                  <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #eee' }}>
                    <Typography sx={{ fontWeight: 700, color: '#e65100' }}>
                      Ocorrências ({totais.ocorrencias || 0})
                    </Typography>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                            Relatório
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Data</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                            Descrição
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {allRelatorios.filter((r) => r.ocorrencias?.length > 0).length > 0 ? (
                          allRelatorios
                            .filter((r) => r.ocorrencias?.length > 0)
                            .flatMap((rel) =>
                              rel.ocorrencias.map((oc, j) => (
                                <TableRow key={`${rel._id}-oc-${j}`} hover>
                                  <TableCell sx={{ fontSize: '0.8rem', color: '#1976d2' }}>
                                    #{rel.numero}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.8rem' }}>{rel.data}</TableCell>
                                  <TableCell sx={{ fontSize: '0.8rem' }}>
                                    {oc.descricao || oc.titulo || JSON.stringify(oc).slice(0, 120)}
                                  </TableCell>
                                </TableRow>
                              ))
                            )
                        ) : (
                          <TableRow>
                            <TableCell colSpan={3} sx={{ textAlign: 'center', py: 3 }}>
                              <Typography variant="body2" color="text.secondary">
                                Nenhuma ocorrência encontrada.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}

              {/* ─── COMENTÁRIOS (todos) ─── */}
              {section === 'comentarios' && (
                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                  <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #eee' }}>
                    <Typography sx={{ fontWeight: 700, color: '#e65100' }}>
                      Comentários ({totais.comentarios || 0})
                    </Typography>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                            Relatório
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Data</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                            Comentário
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {allRelatorios.filter((r) => r.comentarios?.length > 0).length > 0 ? (
                          allRelatorios
                            .filter((r) => r.comentarios?.length > 0)
                            .flatMap((rel) =>
                              rel.comentarios.map((cm, j) => (
                                <TableRow key={`${rel._id}-cm-${j}`} hover>
                                  <TableCell sx={{ fontSize: '0.8rem', color: '#1976d2' }}>
                                    #{rel.numero}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.8rem' }}>{rel.data}</TableCell>
                                  <TableCell sx={{ fontSize: '0.8rem' }}>
                                    {cm.descricao ||
                                      cm.texto ||
                                      cm.comentario ||
                                      JSON.stringify(cm).slice(0, 120)}
                                  </TableCell>
                                </TableRow>
                              ))
                            )
                        ) : (
                          <TableRow>
                            <TableCell colSpan={3} sx={{ textAlign: 'center', py: 3 }}>
                              <Typography variant="body2" color="text.secondary">
                                Nenhum comentário encontrado.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}

              {/* ─── FILTRO DE BUSCA ─── */}
              {section === 'filtro' && (
                <Paper variant="outlined" sx={{ borderRadius: 2, p: 3, textAlign: 'center' }}>
                  <FilterListIcon sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Filtros de busca estarão disponíveis em breve.
                  </Typography>
                </Paper>
              )}
            </>
          )}

          <Dialog
            open={!!pdfDialogUrl || !!pdfError}
            onClose={handleClosePdfDialog}
            maxWidth="lg"
            fullWidth
          >
            <DialogTitle
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 1.5,
              }}
            >
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Relatório #{selectedRelatorio?.numero || 'PDF'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Pré-visualização para impressão
                </Typography>
              </Box>
              <IconButton size="small" onClick={handleClosePdfDialog}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0, bgcolor: '#f5f5f5' }}>
              {pdfLoading ? (
                <Box
                  sx={{
                    height: '75vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 1.5,
                  }}
                >
                  <CircularProgress size={28} />
                  <Typography variant="body2" color="text.secondary">
                    Gerando PDF (pode levar alguns minutos)...
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    O servidor está processando. Aguarde.
                  </Typography>
                </Box>
              ) : pdfError ? (
                <Box
                  sx={{
                    height: '75vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 1.5,
                    px: 2,
                  }}
                >
                  <Typography variant="body2" color="error" sx={{ textAlign: 'center' }}>
                    {pdfError}
                  </Typography>
                  {selectedRelatorio?.linkPdf && (
                    <Button
                      variant="outlined"
                      onClick={() =>
                        window.open(selectedRelatorio.linkPdf, '_blank', 'noopener,noreferrer')
                      }
                    >
                      Abrir PDF em nova aba
                    </Button>
                  )}
                </Box>
              ) : pdfDialogUrl && pdfDialogUrl !== 'loading' ? (
                <Box
                  component="iframe"
                  ref={pdfFrameRef}
                  src={pdfDialogUrl}
                  title={`Relatório ${selectedRelatorio?.numero || ''}`}
                  sx={{ width: '100%', height: '75vh', border: 0, bgcolor: '#fff' }}
                />
              ) : null}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClosePdfDialog}>Fechar</Button>
              <Button
                onClick={handleReloadPdf}
                variant="outlined"
                color="warning"
                disabled={pdfLoading}
                startIcon={<RefreshIcon />}
              >
                Recarregar
              </Button>
              <Button
                onClick={handlePrintPdf}
                variant="contained"
                color="primary"
                disabled={!pdfDialogUrl || pdfDialogUrl === 'loading' || pdfLoading || !!pdfError}
              >
                Imprimir
              </Button>
            </DialogActions>
          </Dialog>
        </Grid>
      </Grid>
    </Box>
  );
}

/* ─── Subcomponente: Empresa (layout visual) ─── */
function EmpresaView({ data }) {
  const empresa = data || {};
  const logoUrl = empresa.logoUrl || empresa.logo || empresa.logomarcaUrl || '';
  const config = empresa.configuracoes || empresa.config || {};

  const checkedValue = (directValue, fallbackValue = false) => {
    if (typeof directValue === 'boolean') return directValue;
    if (typeof directValue === 'number') return directValue > 0;
    if (typeof directValue === 'string')
      return directValue.toLowerCase() === 'true' || directValue === '1';
    return fallbackValue;
  };

  const opcoes = [
    {
      label: "Habilitar opção 'Copiar informações de um relatório já existente'",
      value: checkedValue(
        empresa.habilitarCopiaRelatorio ??
          empresa.habilitarCopiarInformacoes ??
          config.habilitarCopiaRelatorio,
        true
      ),
    },
    {
      label: 'Reabrir relatório aprovado (Aprovação eletrônica)',
      value: checkedValue(empresa.reabrirRelatorioAprovado ?? config.reabrirRelatorioAprovado),
    },
    {
      label: 'Bloquear edição do relatório, após aprovação parcial (Aprovação eletrônica)',
      value: checkedValue(empresa.bloquearEdicaoRelatorio ?? config.bloquearEdicaoRelatorio),
    },
    {
      label: 'Bloquear edição de comentário de outros usuários',
      value: checkedValue(
        empresa.bloquearComentarioOutrosUsuarios ?? config.bloquearComentarioOutrosUsuarios
      ),
    },
  ];

  return (
    <Box sx={{ maxWidth: 980, mx: 'auto' }}>
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #eee' }}>
          <Typography sx={{ fontWeight: 700, color: '#e65100', fontSize: '1.2rem' }}>
            Informações da empresa
          </Typography>
        </Box>

        <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: '#fafafa' }}>
          <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, borderRadius: 1.5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 106,
                  height: 106,
                  borderRadius: 1,
                  bgcolor: '#fff',
                  border: '1px solid #ddd',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}
              >
                {logoUrl ? (
                  <Box
                    component="img"
                    src={logoUrl}
                    alt="Logomarca"
                    sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                ) : (
                  <BusinessIcon sx={{ fontSize: 48, color: '#9e9e9e' }} />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary">
                Logomarca
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" variant="contained" disabled>
                  Adicionar
                </Button>
                <Button size="small" variant="contained" color="error" disabled>
                  Excluir
                </Button>
              </Box>
            </Box>
          </Paper>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Nome da empresa"
                value={empresa.nome || '—'}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                size="small"
                label="Razão social"
                value={empresa.razaoSocial || '—'}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                label="CNPJ"
                value={empresa.cpfCnpj || '—'}
                InputProps={{ readOnly: true }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography sx={{ fontWeight: 700, mb: 0.75 }}>Configurações</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {opcoes.map((op) => (
              <FormControlLabel
                key={op.label}
                control={<Checkbox checked={!!op.value} disabled />}
                label={op.label}
                sx={{ m: 0 }}
              />
            ))}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

const ENDPOINTS = [
  { id: 'empresa', label: 'Empresa', path: '/empresa', icon: BusinessIcon },
  { id: 'cadastros', label: 'Cadastros', path: '/cadastros', icon: ListAltIcon },
  { id: 'obras', label: 'Obras', path: '/obras', icon: ConstructionIcon },
];

export default function DiarioDeObraIntegration() {
  const [token, setToken] = useState(() => localStorage.getItem('diariodeobra_token') || '');
  const [isConnected, setIsConnected] = useState(false);
  const [daysRemaining, setDaysRemaining] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [cardData, setCardData] = useState({});
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = (endpoint) => {
    setSelectedEndpoint(endpoint);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedEndpoint(null);
  };

  useEffect(() => {
    const savedToken = localStorage.getItem('diariodeobra_token');
    const savedExpiry = localStorage.getItem('diariodeobra_token_expiry');

    if (savedToken && savedExpiry) {
      const now = new Date().getTime();
      const expiry = parseInt(savedExpiry, 10);
      if (now > expiry) {
        localStorage.removeItem('diariodeobra_token');
        localStorage.removeItem('diariodeobra_token_expiry');
        setToken('');
      } else {
        setIsConnected(true);
        setDaysRemaining(Math.ceil((expiry - now) / (1000 * 60 * 60 * 24)));
      }
    }
  }, []);

  const fetchAllData = useCallback(async () => {
    setIsLoading(true);
    setLastSync(new Date().toLocaleTimeString('pt-BR'));

    const newData = {};
    await Promise.all(
      ENDPOINTS.map(async (ep) => {
        try {
          const res = await fetch(`${API_BASE}${ep.path}`, {
            headers: { token, 'Content-Type': 'application/json' },
          });
          if (res.ok) {
            const data = await res.json();
            newData[ep.id] = { status: 'ok', data };
          } else {
            newData[ep.id] = { status: 'error', code: res.status };
          }
        } catch {
          newData[ep.id] = { status: 'cors_or_network_error' };
        }
      })
    );

    setCardData((prev) => ({ ...prev, ...newData }));
    setIsLoading(false);
  }, [token]);

  useEffect(() => {
    let interval;
    if (isConnected && token) {
      fetchAllData();
      interval = setInterval(fetchAllData, 60 * 60 * 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isConnected, token, fetchAllData]);

  const handleConnect = async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      // Testa a conexão batendo em /empresa
      const response = await fetch(`${API_BASE}/empresa`, {
        headers: { token: token, 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        setIsConnected(true);
        const expiry = new Date().getTime() + 180 * 24 * 60 * 60 * 1000;
        localStorage.setItem('diariodeobra_token', token);
        localStorage.setItem('diariodeobra_token_expiry', expiry.toString());
        setDaysRemaining(180);
      } else {
        setIsConnected(false);
        alert('Credencial inválida ou erro na API.');
      }
    } catch (e) {
      console.error(e);
      alert('Erro de conexão (pode ser bloqueio de CORS).');
      setIsConnected(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisconnect = () => {
    setIsConnected(false);
    setToken('');
    localStorage.removeItem('diariodeobra_token');
    localStorage.removeItem('diariodeobra_token_expiry');
    setDaysRemaining(null);
    setCardData({});
  };

  return (
    <Card sx={{ mt: 4, borderRadius: 3, boxShadow: '0 8px 24px rgba(0,0,0,0.08)' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}
          >
            Integração API Diário de Obra
            {isConnected ? (
              <CheckCircleIcon sx={{ color: '#4caf50' }} />
            ) : (
              <CancelIcon sx={{ color: '#f44336' }} />
            )}
          </Typography>
          {lastSync && isConnected && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <SyncIcon fontSize="small" /> Última sinc.: {lastSync}
            </Typography>
          )}
        </Box>

        {/* Formulário de Login / Credenciais */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 4 }}>
          <TextField
            label="Token de Acesso (Credencial)"
            variant="outlined"
            size="small"
            fullWidth
            value={token}
            onChange={(e) => setToken(e.target.value)}
            disabled={isConnected || isLoading}
            placeholder="Cole aqui o seu Token JWT"
          />
          {!isConnected ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleConnect}
              disabled={!token || isLoading}
              sx={{ minWidth: 120, fontWeight: 600 }}
            >
              {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Conectar'}
            </Button>
          ) : (
            <Button
              variant="outlined"
              color="error"
              onClick={handleDisconnect}
              sx={{ minWidth: 120, fontWeight: 600 }}
            >
              Desconectar
            </Button>
          )}
        </Box>

        {/* Status de Expiração */}
        {isConnected && daysRemaining !== null && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Status da Conexão da API Diário de Obra: Sessão expira em
            </Typography>
            <Typography
              variant="body2"
              fontWeight="bold"
              color={daysRemaining <= 15 ? 'error.main' : 'success.main'}
            >
              {daysRemaining} {daysRemaining === 1 ? 'dia' : 'dias'}
            </Typography>
          </Box>
        )}

        {/* Cards das Funções da API */}
        <Collapse in={isConnected}>
          <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="stretch" justifyContent="center">
            {ENDPOINTS.map((ep) => (
              <Grid item xs={12} sm="auto" key={ep.id}>
                <Card
                  onClick={() => handleOpenModal(ep)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleOpenModal(ep);
                    }
                  }}
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    borderRadius: 4,
                    boxShadow: '0 12px 28px rgba(0,0,0,0.10)',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: '0 20px 44px rgba(0,0,0,0.14)',
                    },
                    width: 150,
                    height: 150,
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    mx: 'auto',
                  }}
                >
                  <CardContent
                    sx={{
                      p: 0,
                      pt: '10px',
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexGrow: 1,
                      width: '100%',
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 60,
                        height: 60,
                        mb: '10px',
                        bgcolor:
                          ep.id === 'empresa'
                            ? '#3f7de8'
                            : ep.id === 'cadastros'
                              ? '#12a39a'
                              : '#37b4ac',
                        alignSelf: 'center',
                      }}
                    >
                      <ep.icon sx={{ fontSize: 32, color: '#fff' }} />
                    </Avatar>

                    <Typography
                      component="div"
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: '0.95rem', sm: '1rem' },
                        lineHeight: 1.1,
                        overflow: 'hidden',
                        height: 36,
                        textAlign: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: 1,
                      }}
                    >
                      {ep.label}
                    </Typography>

                    <Box
                      sx={{
                        height: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mt: 0.25,
                        px: 1,
                        pb: 0.75,
                      }}
                    >
                      {!cardData[ep.id] ? (
                        <CircularProgress size={16} />
                      ) : cardData[ep.id].status === 'ok' ? (
                        <Chip
                          label="SINCRONIZADO"
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.6rem',
                            fontWeight: 700,
                            bgcolor: '#e8f5e9',
                            color: '#2e7d32',
                          }}
                        />
                      ) : (
                        <Chip
                          label="ERRO"
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.6rem',
                            fontWeight: 700,
                            bgcolor: '#ffebee',
                            color: '#c62828',
                          }}
                        />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Collapse>
      </CardContent>

      <Dialog open={isModalOpen} onClose={handleCloseModal} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          {selectedEndpoint?.label} {selectedEndpoint ? `(GET ${selectedEndpoint.path})` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {selectedEndpoint && cardData[selectedEndpoint.id]?.status === 'ok' ? (
            selectedEndpoint.id === 'empresa' ? (
              <EmpresaView data={cardData[selectedEndpoint.id]?.data} />
            ) : selectedEndpoint.id === 'cadastros' ? (
              <CadastrosView data={cardData[selectedEndpoint.id]?.data} />
            ) : selectedEndpoint.id === 'obras' ? (
              <ObrasView data={cardData[selectedEndpoint.id]?.data} token={token} />
            ) : (
              <Box
                component="pre"
                sx={{
                  bgcolor: 'grey.100',
                  color: 'grey.900',
                  p: 2,
                  borderRadius: 1,
                  overflowX: 'auto',
                  fontSize: '0.875rem',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  m: 0,
                }}
              >
                {JSON.stringify(cardData[selectedEndpoint.id]?.data, null, 2)}
              </Box>
            )
          ) : selectedEndpoint && cardData[selectedEndpoint.id]?.status === 'error' ? (
            <Typography color="error">
              Erro ao carregar dados: {cardData[selectedEndpoint.id]?.error || 'Erro de conexão'}
            </Typography>
          ) : (
            <Typography color="textSecondary">
              Nenhuma informação disponível no momento. Se conecte na API para carregar.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} variant="contained" color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
